import { Button, IconButton } from '@chakra-ui/react'
import { faCommentAlt } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import type { ReactElement } from 'react'

import { pluralize } from '@/utils/helpers'

import { useAppContext } from '@/client/context/app'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { getSeriesChatsCloseDate } from '@/client/services/conversation'
import { getMessagesUrl } from '@/client/services/url'

import Tippy from '@/components/UI/Tippy'

export default function SeriesChatTutorsButton({
  seriesId,
  countTutors,
  seriesEndDate,
  onlyIcon = false,
}: {
  seriesId: number
  countTutors: number
  seriesEndDate: DateTime | null
  onlyIcon?: boolean
}): ReactElement | null {
  const router = useRouter()
  const { requestTimestamp } = useAppContext()
  const now = DateTime.fromISO(requestTimestamp)

  const [handleChatCallback, isLoading] = useAPIWrapper(
    async () => {
      const conversationUuid = await RPC.startSeriesLearnerTutorChat({
        seriesId,
      })
      await router.push(getMessagesUrl(conversationUuid))
    },
    [seriesId, router],
    'Start Series Chat'
  )

  if (seriesEndDate != null && now >= getSeriesChatsCloseDate(seriesEndDate.toJSDate())) {
    // Series chats are closed.
    return null
  }

  return onlyIcon ? (
    <Tippy content="Send this person a message!">
      <IconButton
        aria-label="Send this person a direct message"
        onClick={handleChatCallback}
        icon={<FontAwesomeIcon icon={faCommentAlt} />}
        isRound
        variant="icon"
        isLoading={isLoading}
      />
    </Tippy>
  ) : (
    <Button
      variant="outline"
      onClick={handleChatCallback}
      leftIcon={<FontAwesomeIcon icon={faCommentAlt} />}
      isLoading={isLoading}
    >
      Chat with {pluralize(countTutors, 'Tutor', { unitsOnly: true })}
    </Button>
  )
}
